import React, { FC, MouseEvent, RefObject, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Box, CircularProgress, Link, Typography, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import { useDispatch } from 'react-redux';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import Form, { FormDataModel } from '../../../components/Form';
import axios from 'axios';
import { refreshToken } from '../../../utils/redux/reducer/authentication-slice';
import Config from '../../../utils/Config';
import { routes } from '../../../utils/constants/routes';
import { identifiers } from '../../../utils/constants/identifiers';
import { URLS } from '../../../utils/constants/urls';
import { fetchFromStorage } from '../../../utils/storage';
import { removeErrorFieldsFromValues } from '../../../utils/validators';
import AuthPageShell, { AuthPageMessage, authLinkSx, authPrimaryButtonSx } from '../AuthPageShell';
import { FirstTimeChangePasswordForm } from './FirstTimeChangePasswordForm';

const FirstTimeChangePassword: FC = () => {
  const Navigate = useCustomNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const params = useLocation().search;
  const email = new URLSearchParams(params).get('email');
  const accessToken = new URLSearchParams(params).get('access-token');
  const refresh = new URLSearchParams(params).get('refresh-token');
  const orgId = new URLSearchParams(params).get('orgId');

  const changePasswordForm: RefObject<Form | null | undefined> = useRef();
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<AuthPageMessage>({
    display: false,
    severity: null,
    message: '',
  });

  const handleChangePassword = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = changePasswordForm.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const body = removeErrorFieldsFromValues(formData);
    try {
      setHasError(false);
      if (isFormValid) {
        if (body.newPassword !== body.confirmPassword) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.change_password_error_message,
          });
          return;
        }
        setLoading(true);
        const url = Config.BaseURL + URLS.changePassword;
        const response = await axios.put(
          url,
          { email, oldPassword: body.oldPassword, newPassword: body.newPassword },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${fetchFromStorage(identifiers.access_token) || accessToken}`,
            },
          },
        );
        if (response.status === 200) {
          setMessage({
            display: true,
            message: strings.password_reset_success_message,
            severity: 'success',
          });
          dispatch(refreshToken(refresh) as any);
          setTimeout(() => {
            if (orgId === 'null') Navigate(routes.addOrganization);
            else Navigate(routes.bookings);
          }, 500);
        }
      } else {
        setHasError(true);
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error.response?.data?.error_description ??
          error.response?.data?.message ??
          error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!email || !accessToken || !refresh) {
    return (
      <AuthPageShell
        title="Link expired"
        subtitle="Invalid or expired link. Please log in again."
        message={message}
        onCloseNotification={value => setMessage(prev => ({ ...prev, display: value }))}>
        <Link underline="none">
          <Typography
            variant="body2"
            sx={authLinkSx(theme.palette.primary.main)}
            onClick={() => Navigate(routes.login)}>
            Back to Login
          </Typography>
        </Link>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell
      title="Change Password"
      subtitle="Please change your password to continue. Enter your current password and choose a new one."
      message={message}
      onCloseNotification={value => setMessage(prev => ({ ...prev, display: value }))}>
      <Box component="form" onSubmit={handleChangePassword as any}>
        <Form
          hasError={hasError}
          ref={changePasswordForm as RefObject<Form>}
          model={FirstTimeChangePasswordForm(strings)}
          values={{}}
        />
        <PrimaryButton
          data-testid="first-time-change-password-btn"
          disabled={loading}
          sx={authPrimaryButtonSx}
          fullWidth
          type="submit"
          onClick={handleChangePassword}>
          {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
          Change Password
        </PrimaryButton>
      </Box>

      <Link underline="none">
        <Typography
          variant="body2"
          sx={authLinkSx(theme.palette.primary.main)}
          onClick={() => Navigate(routes.login)}>
          Back to Login
        </Typography>
      </Link>
    </AuthPageShell>
  );
};

export default FirstTimeChangePassword;
